import { createPortal } from 'react-dom';

import { AngleSlider } from './sliders/angle-slider';
import { SliceSlider } from './sliders/slice-slider';

import { useViewportSlots } from '@/hooks/use-viewport-slots';
import { isCprViewport, isSliceViewport } from '@/visualization/viewports/viewport-categories';

/**
 * Portals slice and angle sliders into the 'slider' overlay slot of each viewport.
 *
 * - SliceSlider → SLICE_* viewports (SliceViewport2D instances), positioned left
 * - AngleSlider → CPR_*   viewports (CprViewport2D instances),  positioned top
 *
 * Both components are portaled into the single 'slider' slot and position
 * themselves inside it via absolute CSS.
 *
 * Place as a sibling of ViewportContainer inside the VisualizationProvider tree.
 */
export function SliderManager() {
    const viewports = useViewportSlots(['slider']);

    if (viewports.length === 0) return null;

    const portals: React.ReactPortal[] = [];

    for (const vp of viewports) {
        if (vp.slotKind !== 'viewport' || !vp.type || !vp.slots['slider']) continue;

        const container = vp.slots['slider'];

        if (isCprViewport(vp.type)) {
            portals.push(
                createPortal(<AngleSlider viewportId={vp.id} />, container, `angle-slider-${vp.id}`)
            );
        } else if (isSliceViewport(vp.type)) {
            portals.push(
                createPortal(<SliceSlider viewportId={vp.id} />, container, `slice-slider-${vp.id}`)
            );
        }
    }

    if (portals.length === 0) return null;

    return <>{portals}</>;
}
